import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  ActionButtonsRow,
  Result,
  MathDiv,
  Select,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  AlertDiv,
  AdvancedOptionsButton,
  CopyImageButton,
  ImageDiv,
  SvgImage,
  ErrorMessage,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { FaRegCopy } from 'react-icons/fa6'
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa'
import { MdError } from 'react-icons/md'
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
import { Table, Th, Td, Tr, TableContainer, AddButton, ChartLegend } from './styles'

const DIAGRAM_SIZE = 400
const DIAGRAM_MARGIN = 50
const FLAG_HATCH_COUNT = 10

function barAreaCm2(diameterMm) {
  const diameterCm = diameterMm / 10
  return (Math.PI * diameterCm * diameterCm) / 4
}

// Parábola-retângulo com os parâmetros default do back-end (epsC2=0,002, epsCu=0,0035, n=2),
// já que a calculadora não expõe esses coeficientes para o usuário sobrescrever.
function concreteStress(strain, fcd, epsC2 = 0.002, n = 2) {
  if (strain <= 0) return 0
  if (strain >= epsC2) return fcd
  return fcd * (1 - Math.pow(1 - strain / epsC2, n))
}

export function ConcreteObliqueFlexCompression() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(30)
  const [fyk, setFyk] = useState(500)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [gammaC, setGammaC] = useState(1.4)
  const [gammaS, setGammaS] = useState(1.15)
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [diameterUnit, setDiameterUnit] = useState('mm')
  const [barDiameter, setBarDiameter] = useState('')
  const [barCoverage, setBarCoverage] = useState('')
  const [barNx, setBarNx] = useState('')
  const [barNy, setBarNy] = useState('')
  const [bars, setBars] = useState([])
  const [nSd, setNSd] = useState('')
  const [mxSd, setMxSd] = useState('')
  const [mySd, setMySd] = useState('')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-oblique-flex-compression-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setGammaC(entry.gammaC || 1.4)
      setGammaS(entry.gammaS || 1.15)
      setB(entry.b)
      setH(entry.h)
      setDiameterUnit(entry.diameterUnit || 'mm')
      setBars(entry.bars || [])
      setNSd(entry.nSd)
      setMxSd(entry.mxSd)
      setMySd(entry.mySd)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (!['nSd', 'mxSd', 'mySd'].includes(name) && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.gammaC = validateField('gammaC', gammaC)
    newErrors.gammaS = validateField('gammaS', gammaS)
    newErrors.b = validateField('b', b)
    newErrors.h = validateField('h', h)
    newErrors.nSd = validateField('nSd', nSd)
    newErrors.mxSd = validateField('mxSd', mxSd)
    newErrors.mySd = validateField('mySd', mySd)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors && bars.length > 0)
  }, [fck, fyk, gammaC, gammaS, b, h, nSd, mxSd, mySd, bars, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  function handleAddBar(event) {
    event.preventDefault()

    const diameterMm = diameterUnit === 'mm' ? Number(barDiameter) : Number(barDiameter) * 10
    const nx = Number(barNx)
    const ny = Number(barNy)
    const d = Number(barCoverage)

    const newBars = []

    // Coordenadas centradas: (0,0) no centro da seção
    // xMin = -b/2, xMax = b/2; yMin = -h/2, yMax = h/2
    const xRange = b / 2 - d
    const yRange = h / 2 - d

    // Calcular posições x
    const xPositions = []
    if (nx === 1) {
      xPositions.push(0)
    } else {
      for (let i = 0; i < nx; i++) {
        const x = -xRange + (i * 2 * xRange) / (nx - 1)
        xPositions.push(roundToDecimals(x, 2))
      }
    }

    // Calcular posições y
    const yPositions = []
    if (ny === 1) {
      yPositions.push(0)
    } else {
      for (let j = 0; j < ny; j++) {
        const y = -yRange + (j * 2 * yRange) / (ny - 1)
        yPositions.push(roundToDecimals(y, 2))
      }
    }

    // Preencher barras em grade completa nx × ny
    for (let j = 0; j < yPositions.length; j++) {
      const y = yPositions[j]
      for (let i = 0; i < xPositions.length; i++) {
        newBars.push({ x: xPositions[i], y, diameterMm })
      }
    }

    setBars(newBars)
    setBarDiameter('')
    setBarCoverage('')
    setBarNx('')
    setBarNy('')
    toast.success(`${newBars.length} barra${newBars.length > 1 ? 's' : ''} adicionada${newBars.length > 1 ? 's' : ''}`)
  }

  function handleRemoveBar(event, indexToRemove) {
    event.preventDefault()

    setBars(bars.filter((_, index) => index !== indexToRemove))
    toast.success('Barra removida')
  }

  const convertToStandardUnits = () => {
    return {
      b,
      h,
      concrete: { fck, gammaC },
      steel: { fyk, gammaS },
      bars: bars.map((bar) => ({
        x: roundToDecimals(b / 2 + bar.x, 2),
        y: roundToDecimals(h / 2 + bar.y, 2),
        As: roundToDecimals(barAreaCm2(bar.diameterMm), 4),
      })),
      loads: {
        nSd,
        mxSd,
        mySd,
      },
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = convertToStandardUnits()

      setData(data)

      const response = await api.post('/concrete/oblique-flex-compression', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        gammaC,
        gammaS,
        b,
        h,
        diameterUnit,
        bars,
        nSd,
        mxSd,
        mySd,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      gammaC,
      gammaS,
      b,
      h,
      diameterUnit,
      bars,
      nSd,
      mxSd,
      mySd,
      result,
      data,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Flexo-Compressão Oblíqua.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setGammaC(jsonData.gammaC || 1.4)
        setGammaS(jsonData.gammaS || 1.15)
        setB(jsonData.b)
        setH(jsonData.h)
        setDiameterUnit(jsonData.diameterUnit || 'mm')
        setBars(jsonData.bars || [])
        setNSd(jsonData.nSd)
        setMxSd(jsonData.mxSd)
        setMySd(jsonData.mySd)
        setResult(jsonData.result)
        setData(jsonData.data)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const handleCopySVG = async (svgRef) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = DIAGRAM_SIZE
      canvas.height = DIAGRAM_SIZE
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const inputImageRef = useRef(null)
  const stressDiagramRef = useRef(null)

  function sectionProjection(sectionB, sectionH) {
    const available = DIAGRAM_SIZE - DIAGRAM_MARGIN * 2
    const scale = sectionB > 0 && sectionH > 0 ? Math.min(available / sectionB, available / sectionH) : 1
    const rectW = sectionB * scale
    const rectH = sectionH * scale
    const originX = (DIAGRAM_SIZE - rectW) / 2
    const originY = (DIAGRAM_SIZE - rectH) / 2

    return {
      rectW,
      rectH,
      originX,
      originY,
      px: (x) => originX + x * scale,
      py: (y) => originY + y * scale,
    }
  }

  const InputImage = () => {
    const sectionB = b !== '' && b > 0 ? Number(b) : 20
    const sectionH = h !== '' && h > 0 ? Number(h) : 30
    const { rectW, rectH, originX, originY, px, py } = sectionProjection(sectionB, sectionH)

    return (
      <SvgImage
        ref={inputImageRef}
        viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <rect x={originX} y={originY} width={rectW} height={rectH} stroke="black" strokeWidth="2" fill="none" />
        <line x1={originX} y1={originY} x2={originX + 30} y2={originY} stroke="black" strokeWidth="1.5" />
        <line x1={originX} y1={originY} x2={originX} y2={originY + 30} stroke="black" strokeWidth="1.5" />
        <text x={originX + 34} y={originY + 4} fontFamily="Poppins" fontSize="14">
          x
        </text>
        <text x={originX - 4} y={originY + 42} fontFamily="Poppins" fontSize="14">
          y
        </text>
        {bars.map((bar, index) => (
          <circle key={index} cx={px(sectionB / 2 + bar.x)} cy={py(sectionH / 2 + bar.y)} r={5} fill="black" />
        ))}
        <text x={originX + rectW / 2} y={originY + rectH + 24} fontFamily="Poppins" fontSize="16" textAnchor="middle">
          b {b !== '' && b != 0 ? `= ${b} cm` : ''}
        </text>
        <text
          transform={`translate(${originX - 16} ${originY + rectH / 2}) rotate(-90)`}
          fontFamily="Poppins"
          fontSize="16"
          textAnchor="middle"
        >
          h {h !== '' && h != 0 ? `= ${h} cm` : ''}
        </text>
      </SvgImage>
    )
  }

  function StressDiagram({ stressDiagram }) {
    const {
      section,
      bars: stressBars,
      neutralAxis,
      compressedZone,
      strains,
      maxConcreteCompressionStress,
    } = stressDiagram

    const fcd = maxConcreteCompressionStress.value
    const maxDim = Math.max(section.b, section.h)
    const hasFlag = Boolean(neutralAxis.line) && compressedZone.polygon.length > 0

    // A seção é mantida ortogonal (sem rotação). A linha neutra é desenhada inclinada e o
    // diagrama de tensões fica apoiado sobre a face comprimida da seção, com as ordenadas de
    // tensão saindo perpendicularmente a essa face (parábola-retângulo), como no software de
    // referência.
    let flag = null
    if (hasFlag) {
      const magNormal = Math.hypot(neutralAxis.normal.x, neutralAxis.normal.y) || 1
      // normal aponta para o lado comprimido (na direção da fibra mais comprimida)
      const nx = neutralAxis.normal.x / magNormal
      const ny = neutralAxis.normal.y / magNormal

      const p1 = neutralAxis.line.p1
      const center = { x: section.b / 2, y: section.h / 2 }

      // fibra mais comprimida: vértice da seção com maior projeção na direção da normal
      const extreme = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj > best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremePoint = extreme.point
      // profundidade da zona comprimida (distância perpendicular da LN à fibra extrema)
      const depth = (extremePoint.x - p1.x) * nx + (extremePoint.y - p1.y) * ny

      // ponto onde a linha neutra cruza a borda da seção adjacente à fibra extrema:
      // é o extremo da face comprimida onde a tensão vale zero
      const eps = 0.5
      const crossingOnEdge = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremePoint.x) < eps || Math.abs(crossing.y - extremePoint.y) < eps
      )

      if (depth > 0.01 && crossingOnEdge) {
        // vetor ao longo da face comprimida (da LN até a fibra extrema)
        const edgeVecX = extremePoint.x - crossingOnEdge.x
        const edgeVecY = extremePoint.y - crossingOnEdge.y
        const midX = (crossingOnEdge.x + extremePoint.x) / 2
        const midY = (crossingOnEdge.y + extremePoint.y) / 2
        // as ordenadas de tensão seguem a orientação da linha neutra (paralelas à LN),
        // apontando para fora da seção — o bloco de tensões fica "deitado" na direção da LN
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY < 0) {
          outX = -outX
          outY = -outY
        }

        const flagLen = 0.42 * maxDim
        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          // ponto sobre a face comprimida
          const bx = crossingOnEdge.x + s * edgeVecX
          const by = crossingOnEdge.y + s * edgeVecY
          // tensão nesse ponto conforme sua distância perpendicular à linha neutra
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.compressedExtreme * dist) / depth
          const stress = concreteStress(strain, fcd)
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (stress / fcd) * flagLen * outX, y: by + (stress / fcd) * flagLen * outY })
        }

        // segunda linha tracejada: paralela à LN passando pela fibra extrema
        const naDirX = neutralAxis.line.p2.x - neutralAxis.line.p1.x
        const naDirY = neutralAxis.line.p2.y - neutralAxis.line.p1.y
        const naDirLen = Math.hypot(naDirX, naDirY) || 1
        const secondLine = {
          p1: {
            x: extremePoint.x - (0.45 * maxDim * naDirX) / naDirLen,
            y: extremePoint.y - (0.45 * maxDim * naDirY) / naDirLen,
          },
          p2: {
            x: extremePoint.x + (0.25 * maxDim * naDirX) / naDirLen,
            y: extremePoint.y + (0.25 * maxDim * naDirY) / naDirLen,
          },
        }

        flag = { baseline, profile, tip: profile[profile.length - 1], secondLine }
      }
    }

    // linha neutra estendida para além da seção
    const naExtended = hasFlag
      ? (() => {
          const p1 = neutralAxis.line.p1
          const p2 = neutralAxis.line.p2
          const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
          const dx = (p2.x - p1.x) / len
          const dy = (p2.y - p1.y) / len
          const ext = 0.2 * maxDim
          return {
            p1: { x: p1.x - ext * dx, y: p1.y - ext * dy },
            p2: { x: p2.x + ext * dx, y: p2.y + ext * dy },
          }
        })()
      : null

    // bounding box de tudo que será desenhado, para escalar e centralizar
    const allPoints = [...section.polygon]
    if (flag) {
      allPoints.push(...flag.baseline, ...flag.profile, flag.secondLine.p1, flag.secondLine.p2)
    }
    if (naExtended) {
      allPoints.push(naExtended.p1, naExtended.p2)
    }
    const xs = allPoints.map((point) => point.x)
    const ys = allPoints.map((point) => point.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const pad = 0.08 * maxDim
    const contentW = maxX - minX + 2 * pad
    const contentH = maxY - minY + 2 * pad
    const available = DIAGRAM_SIZE - DIAGRAM_MARGIN * 2
    const scale = contentW > 0 && contentH > 0 ? Math.min(available / contentW, available / contentH) : 1
    const offsetX = (DIAGRAM_SIZE - (maxX - minX) * scale) / 2 - minX * scale
    const offsetY = (DIAGRAM_SIZE - (maxY - minY) * scale) / 2 - minY * scale
    const px = (x) => offsetX + x * scale
    const py = (y) => offsetY + y * scale

    const toPoints = (points) => points.map((point) => `${px(point.x)},${py(point.y)}`).join(' ')

    return (
      <SvgImage
        ref={stressDiagramRef}
        viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <polygon points={toPoints(section.polygon)} fill="#d1d5db" stroke="black" strokeWidth="2" />
        {naExtended && (
          <line
            x1={px(naExtended.p1.x)}
            y1={py(naExtended.p1.y)}
            x2={px(naExtended.p2.x)}
            y2={py(naExtended.p2.y)}
            stroke="#374151"
            strokeWidth="1.5"
            strokeDasharray="7 5"
          />
        )}
        {flag && (
          <>
            <line
              x1={px(flag.secondLine.p1.x)}
              y1={py(flag.secondLine.p1.y)}
              x2={px(flag.secondLine.p2.x)}
              y2={py(flag.secondLine.p2.y)}
              stroke="#374151"
              strokeWidth="1.5"
              strokeDasharray="7 5"
            />
            <polygon
              points={`${toPoints(flag.baseline)} ${toPoints([...flag.profile].reverse())}`}
              fill="#93c5fd"
              fillOpacity="0.6"
              stroke="#1d4ed8"
              strokeWidth="1.5"
            />
            {flag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={index}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(flag.profile[index].x)}
                    y2={py(flag.profile[index].y)}
                    stroke="#1d4ed8"
                    strokeWidth="0.8"
                  />
                )
            )}
            <line
              x1={px(flag.tip.x)}
              y1={py(flag.tip.y)}
              x2={px(flag.tip.x) + 12}
              y2={py(flag.tip.y) - 14}
              stroke="#1d4ed8"
              strokeWidth="1"
            />
            <text x={px(flag.tip.x) + 14} y={py(flag.tip.y) - 12} fontFamily="Poppins" fontSize="13" fill="#1d4ed8">
              fcd = {fcd} MPa
            </text>
          </>
        )}
        {stressBars.map((bar, index) => (
          <circle key={index} cx={px(bar.x)} cy={py(bar.y)} r={5} fill={bar.stress >= 0 ? '#1d4ed8' : '#f97316'} />
        ))}
      </SvgImage>
    )
  }

  function StrainDiagram({ stressDiagram }) {
    const { section, bars: strainBars, neutralAxis, strains } = stressDiagram
    const maxDim = Math.max(section.b, section.h)
    const hasFlag = Boolean(neutralAxis.line)

    let compressionFlag = null
    let tensionFlag = null
    if (hasFlag) {
      const magNormal = Math.hypot(neutralAxis.normal.x, neutralAxis.normal.y) || 1
      const nx = neutralAxis.normal.x / magNormal
      const ny = neutralAxis.normal.y / magNormal

      const p1 = neutralAxis.line.p1
      const center = { x: section.b / 2, y: section.h / 2 }

      // Compressão (maior projeção na normal)
      const extremeComp = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj > best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremeCompPoint = extremeComp.point
      const depthComp = (extremeCompPoint.x - p1.x) * nx + (extremeCompPoint.y - p1.y) * ny

      // Tração (menor projeção na normal)
      const extremeTrac = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj < best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremeTracPoint = extremeTrac.point
      const depthTrac = (extremeTracPoint.x - p1.x) * nx + (extremeTracPoint.y - p1.y) * ny

      const eps = 0.5
      const crossingOnEdgeComp = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremeCompPoint.x) < eps || Math.abs(crossing.y - extremeCompPoint.y) < eps
      )
      const crossingOnEdgeTrac = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremeTracPoint.x) < eps || Math.abs(crossing.y - extremeTracPoint.y) < eps
      )

      const maxStrain = Math.max(Math.abs(strains.compressedExtreme), Math.abs(strains.oppositeExtreme))
      const flagLen = 0.7 * maxDim

      // Diagrama de compressão
      if (depthComp > 0.01 && crossingOnEdgeComp) {
        const edgeVecX = extremeCompPoint.x - crossingOnEdgeComp.x
        const edgeVecY = extremeCompPoint.y - crossingOnEdgeComp.y
        const midX = (crossingOnEdgeComp.x + extremeCompPoint.x) / 2
        const midY = (crossingOnEdgeComp.y + extremeCompPoint.y) / 2
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY < 0) {
          outX = -outX
          outY = -outY
        }

        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          const bx = crossingOnEdgeComp.x + s * edgeVecX
          const by = crossingOnEdgeComp.y + s * edgeVecY
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.compressedExtreme * dist) / depthComp
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (strain / maxStrain) * flagLen * outX, y: by + (strain / maxStrain) * flagLen * outY })
        }
        compressionFlag = { baseline, profile }
      }

      // Diagrama de tração
      if (Math.abs(depthTrac) > 0.01 && crossingOnEdgeTrac) {
        const edgeVecX = extremeTracPoint.x - crossingOnEdgeTrac.x
        const edgeVecY = extremeTracPoint.y - crossingOnEdgeTrac.y
        const midX = (crossingOnEdgeTrac.x + extremeTracPoint.x) / 2
        const midY = (crossingOnEdgeTrac.y + extremeTracPoint.y) / 2
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY > 0) {
          outX = -outX
          outY = -outY
        }

        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          const bx = crossingOnEdgeTrac.x + s * edgeVecX
          const by = crossingOnEdgeTrac.y + s * edgeVecY
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.oppositeExtreme * dist) / depthTrac
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (strain / maxStrain) * flagLen * outX, y: by + (strain / maxStrain) * flagLen * outY })
        }
        tensionFlag = { baseline, profile }
      }
    }

    const naExtended = hasFlag
      ? (() => {
          const p1 = neutralAxis.line.p1
          const p2 = neutralAxis.line.p2
          const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
          const dx = (p2.x - p1.x) / len
          const dy = (p2.y - p1.y) / len
          const ext = 0.2 * maxDim
          return {
            p1: { x: p1.x - ext * dx, y: p1.y - ext * dy },
            p2: { x: p2.x + ext * dx, y: p2.y + ext * dy },
          }
        })()
      : null

    const allPoints = [...section.polygon]
    if (compressionFlag) {
      allPoints.push(...compressionFlag.baseline, ...compressionFlag.profile)
    }
    if (tensionFlag) {
      allPoints.push(...tensionFlag.baseline, ...tensionFlag.profile)
    }
    if (naExtended) {
      allPoints.push(naExtended.p1, naExtended.p2)
    }
    const xs = allPoints.map((point) => point.x)
    const ys = allPoints.map((point) => point.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const pad = 0.08 * maxDim
    const contentW = maxX - minX + 2 * pad
    const contentH = maxY - minY + 2 * pad
    const available = DIAGRAM_SIZE - DIAGRAM_MARGIN * 2
    const scale = contentW > 0 && contentH > 0 ? Math.min(available / contentW, available / contentH) : 1
    const offsetX = (DIAGRAM_SIZE - (maxX - minX) * scale) / 2 - minX * scale
    const offsetY = (DIAGRAM_SIZE - (maxY - minY) * scale) / 2 - minY * scale
    const px = (x) => offsetX + x * scale
    const py = (y) => offsetY + y * scale

    const toPoints = (points) => points.map((point) => `${px(point.x)},${py(point.y)}`).join(' ')

    return (
      <SvgImage viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <polygon points={toPoints(section.polygon)} fill="#d1d5db" stroke="black" strokeWidth="2" />
        {naExtended && (
          <line
            x1={px(naExtended.p1.x)}
            y1={py(naExtended.p1.y)}
            x2={px(naExtended.p2.x)}
            y2={py(naExtended.p2.y)}
            stroke="#374151"
            strokeWidth="1.5"
            strokeDasharray="7 5"
          />
        )}
        {compressionFlag && (
          <>
            <line
              x1={px(compressionFlag.baseline[0].x)}
              y1={py(compressionFlag.baseline[0].y)}
              x2={px(compressionFlag.baseline[compressionFlag.baseline.length - 1].x)}
              y2={py(compressionFlag.baseline[compressionFlag.baseline.length - 1].y)}
              stroke="#6366f1"
              strokeWidth="2"
            />
            <polygon
              points={`${toPoints(compressionFlag.baseline)} ${toPoints([...compressionFlag.profile].reverse())}`}
              fill="#c7d2fe"
              fillOpacity="0.6"
              stroke="#6366f1"
              strokeWidth="1.5"
            />
            {compressionFlag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={`comp-${index}`}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(compressionFlag.profile[index].x)}
                    y2={py(compressionFlag.profile[index].y)}
                    stroke="#6366f1"
                    strokeWidth="0.8"
                  />
                )
            )}
            <text
              x={px(compressionFlag.profile[compressionFlag.profile.length - 1].x) - 25}
              y={py(compressionFlag.profile[compressionFlag.profile.length - 1].y) - 12}
              fontFamily="sans-serif"
              fontSize="13"
              fill="#6366f1"
              fontWeight="bold"
              textAnchor="end"
            >
              {(strains.compressedExtreme * 1000).toFixed(2)}‰
            </text>
          </>
        )}
        {tensionFlag && (
          <>
            <line
              x1={px(tensionFlag.baseline[0].x)}
              y1={py(tensionFlag.baseline[0].y)}
              x2={px(tensionFlag.baseline[tensionFlag.baseline.length - 1].x)}
              y2={py(tensionFlag.baseline[tensionFlag.baseline.length - 1].y)}
              stroke="#f97316"
              strokeWidth="2"
            />
            <polygon
              points={`${toPoints(tensionFlag.baseline)} ${toPoints([...tensionFlag.profile].reverse())}`}
              fill="#fed7aa"
              fillOpacity="0.6"
              stroke="#f97316"
              strokeWidth="1.5"
            />
            {tensionFlag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={`trac-${index}`}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(tensionFlag.profile[index].x)}
                    y2={py(tensionFlag.profile[index].y)}
                    stroke="#f97316"
                    strokeWidth="0.8"
                  />
                )
            )}
            <text
              x={px(tensionFlag.profile[tensionFlag.profile.length - 1].x) + 25}
              y={py(tensionFlag.profile[tensionFlag.profile.length - 1].y) + 16}
              fontFamily="sans-serif"
              fontSize="13"
              fill="#f97316"
              fontWeight="bold"
              textAnchor="start"
            >
              {(strains.oppositeExtreme * 1000).toFixed(2)}‰
            </text>
          </>
        )}
        {strainBars.map((bar, index) => (
          <circle key={index} cx={px(bar.x)} cy={py(bar.y)} r={5} fill={bar.strain >= 0 ? '#6366f1' : '#f97316'} />
        ))}
      </SvgImage>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Flexo-Compressão Oblíqua</span>
          </div>
          <h1>📐 Flexo-Compressão Oblíqua em Concreto Armado</h1>
          <p className="subtitle">
            Verificação de seções retangulares de concreto armado submetidas a força normal e momentos fletores em dois
            eixos, por análise de fibras 2D com envoltória de interação N-Mx-My
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🎯 Fibras 2D</Badge>
            <Badge>📐 Envoltória N-Mx-My</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta verifica uma seção retangular de concreto armado submetida a força normal e momentos
              fletores em dois eixos (flexo-compressão oblíqua). A seção é discretizada em fibras 2D, a envoltória
              resistente N-Mx-My é montada por varredura da linha neutra e o ponto solicitante é comparado a essa
              envoltória.
            </p>
            <p>
              Informe as barras pelo centro de cada uma, no sistema de eixos locais da seção: x cresce da esquerda para
              a direita (0 a b) e y cresce de cima para baixo (0 a h). O resultado traz o índice de utilização, o
              diagrama de interação N-Mx-My e o diagrama de tensões/deformações da seção.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Análise por fibras</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton
              type="button"
              title="Clique para copiar essa imagem"
              onClick={() => handleCopySVG(inputImageRef)}
            >
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <AdvancedOptionsButton
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              $isOpen={showAdvancedOptions}
            >
              <span className="arrow-icon">{showAdvancedOptions ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvancedOptions ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>
            {showAdvancedOptions && (
              <>
                <InputDiv>
                  <span>Coeficiente de ponderação do concreto γc</span>
                  <ValidatedInput
                    name="gammaC"
                    type="number"
                    value={gammaC || ''}
                    onChange={(event) => setGammaC(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de ponderação do aço γs</span>
                  <ValidatedInput
                    name="gammaS"
                    type="number"
                    value={gammaS || ''}
                    onChange={(event) => setGammaS(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura b (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura h (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Armadura</h3>
            <InputDiv>
              <span>Diâmetro das barras ({diameterUnit})</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  value={barDiameter}
                  onChange={(event) => setBarDiameter(event.target.value)}
                  disabled={isLoading}
                  style={{ flex: 1 }}
                />
                <Select
                  value={diameterUnit}
                  onChange={(event) => setDiameterUnit(event.target.value)}
                  disabled={isLoading}
                  style={{ marginLeft: '0', flex: 0.6 }}
                >
                  <option value="mm">mm</option>
                  <option value="cm">cm</option>
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Margem de cobrimento (d&apos;) — cm</span>
              <input
                type="number"
                value={barCoverage}
                onChange={(event) => setBarCoverage(event.target.value)}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px' }}>nx</label>
                  <input
                    type="number"
                    value={barNx}
                    onChange={(event) => setBarNx(event.target.value)}
                    disabled={isLoading}
                    placeholder="Quantidade em x"
                    min="1"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px' }}>ny</label>
                  <input
                    type="number"
                    value={barNy}
                    onChange={(event) => setBarNy(event.target.value)}
                    disabled={isLoading}
                    placeholder="Quantidade em y"
                    min="1"
                  />
                </div>
              </span>
            </InputDiv>
            <InputDiv>
              <AddButton
                type="button"
                onClick={handleAddBar}
                disabled={
                  isLoading ||
                  barDiameter === '' ||
                  barCoverage === '' ||
                  barNx === '' ||
                  barNy === '' ||
                  Number(barDiameter) <= 0
                }
                title="Adicionar armadura"
                style={{ width: '100%', padding: '8px', justifyContent: 'center', gap: '8px' }}
              >
                <FaPlusCircle />
                Adicionar armadura
              </AddButton>
            </InputDiv>
            {bars.length === 0 ? (
              <ErrorMessage>Adicione ao menos uma barra à seção</ErrorMessage>
            ) : (
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>x (cm)</Th>
                      <Th>y (cm)</Th>
                      <Th>Ø (mm)</Th>
                      <Th>As (cm²)</Th>
                      <Th>Remover</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bars.map((bar, index) => (
                      <Tr key={index}>
                        <Td>{index + 1}</Td>
                        <Td>{roundToDecimals(b / 2 + bar.x, 2)}</Td>
                        <Td>{roundToDecimals(h / 2 + bar.y, 2)}</Td>
                        <Td>{bar.diameterMm}</Td>
                        <Td>{roundToDecimals(barAreaCm2(bar.diameterMm), 4)}</Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button type="button" onClick={(event) => handleRemoveBar(event, index)} disabled={isLoading}>
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
            <h3>Esforços</h3>
            <InputDiv>
              <span>Força Normal Solicitante Nd (kN) — compressão positiva</span>
              <ValidatedInput
                name="nSd"
                type="number"
                value={nSd}
                onChange={(event) => setNSd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Momento Solicitante Mxd (kN.cm)</span>
              <ValidatedInput
                name="mxSd"
                type="number"
                value={mxSd}
                onChange={(event) => setMxSd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Momento Solicitante Myd (kN.cm)</span>
              <ValidatedInput
                name="mySd"
                type="number"
                value={mySd}
                onChange={(event) => setMySd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {!result.verification ? (
                <MathDiv>
                  <AlertDiv>
                    <p>{result.reason || 'Não foi possível verificar a seção com os dados informados.'}</p>
                    <MdError />
                  </AlertDiv>
                </MathDiv>
              ) : (
                <>
                  <MathDiv>
                    <BlockMath
                      math={`N_{Sd}= ${result.verification.nSd} \\, \\text{kN} \\qquad M_{xSd}= ${result.verification.mxSd} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{ySd}= ${result.verification.mySd} \\, \\text{kN}\\cdot\\text{cm}`}
                    />
                    <BlockMath
                      math={`M_{Sd}= ${result.verification.mSd} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{Rd}= ${result.verification.mRd} \\, \\text{kN}\\cdot\\text{cm}`}
                    />
                  </MathDiv>
                  <MathDiv>
                    {result.verification.ok ? (
                      <BlockMath
                        math={`\\dfrac{M_{Sd}}{M_{Rd}}= ${result.verification.ratio} \\leq 1 \\rightarrow \\text{Seção verificada (utilização de } ${(result.verification.ratio * 100).toFixed(1)}\\%\\text{)}`}
                      />
                    ) : (
                      <AlertDiv>
                        <BlockMath
                          math={`\\dfrac{M_{Sd}}{M_{Rd}}= ${result.verification.ratio} > 1 \\rightarrow \\text{Seção não verificada}`}
                        />
                        <MdError />
                      </AlertDiv>
                    )}
                  </MathDiv>
                  {result.verification.fs && (
                    <MathDiv>
                      <BlockMath math={`F.S = ${roundToDecimals(result.verification.fs, 2)}`} />
                    </MathDiv>
                  )}
                </>
              )}
            </Result>
            {result.verification && (
              <Result className="results-section" $isRecalculating={isRecalculating}>
                {isRecalculating && <LoadingOverlay />}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <h2>Cálculos</h2>
                  <CopyHTMLButton type="button" onClick={handleCopyResults}>
                    📋 Copiar Cálculos
                  </CopyHTMLButton>
                </div>
                <h3>Dados da seção</h3>
                <MathDiv>
                  <BlockMath math={`b= ${data.b} \\, \\text{cm} \\qquad h= ${data.h} \\, \\text{cm}`} />
                  <BlockMath
                    math={`f_{ck}= ${data.concrete.fck} \\, \\text{MPa} \\qquad f_{yk}= ${data.steel.fyk} \\, \\text{MPa}`}
                  />
                  <BlockMath math={`n_{barras}= ${data.bars.length}`} />
                </MathDiv>
                {result.verification.limitState && (
                  <>
                    <h3>Estado limite da seção</h3>
                    <MathDiv>
                      <BlockMath
                        math={`\\theta= ${result.verification.limitState.theta} \\, \\text{rad} = ${roundToDecimals((result.verification.limitState.theta * 180) / Math.PI)}°`}
                      />
                      <BlockMath
                        math={`x= ${result.verification.limitState.neutralAxisDepth} \\, \\text{cm} \\quad \\text{(profundidade da linha neutra)}`}
                      />
                      <BlockMath
                        math={`\\epsilon_{comp}= ${(result.verification.limitState.compressedExtremeStrain * 1000).toFixed(2)} \\text{ permil} \\qquad \\epsilon_{op}= ${(result.verification.limitState.oppositeExtremeStrain * 1000).toFixed(2)} \\text{ permil}`}
                      />
                      <BlockMath
                        math={`\\sigma_{c,max}= ${result.verification.limitState.maxConcreteCompressionStress} \\, \\text{MPa}`}
                      />
                    </MathDiv>
                  </>
                )}
                <h3>Diagrama de interação N-Mx-My</h3>
                <ChartLegend>
                  <span>
                    <span className="swatch line" style={{ background: '#2563eb' }} /> Envoltória resistente (
                    {result.verification.diagram.force.label} = {result.verification.diagram.force.value}{' '}
                    {result.verification.diagram.force.unit})
                  </span>
                  <span>
                    <span className="swatch" style={{ background: '#16a34a' }} /> Ponto solicitante
                  </span>
                </ChartLegend>
                <ResponsiveContainer width="100%" height={380}>
                  <ComposedChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={['auto', 'auto']}
                      label={{
                        value: `${result.verification.diagram.yAxis.label} (${result.verification.diagram.yAxis.unit})`,
                        position: 'insideBottom',
                        offset: -10,
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      domain={['auto', 'auto']}
                      label={{
                        value: `${result.verification.diagram.xAxis.label} (${result.verification.diagram.xAxis.unit})`,
                        angle: -90,
                        position: 'insideLeft',
                      }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0) {
                          const data = payload[0].payload
                          return (
                            <div
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                                {result.verification.diagram.yAxis.label}: {roundToDecimals(data.x, 2)}
                              </p>
                              <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                                {result.verification.diagram.xAxis.label}: {roundToDecimals(data.y, 2)}
                              </p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Line
                      data={
                        result.verification.diagram.closedCurve && result.verification.diagram.curve.length > 0
                          ? [...result.verification.diagram.curve, result.verification.diagram.curve[0]]
                          : result.verification.diagram.curve
                      }
                      type="linear"
                      dataKey="y"
                      name="Envoltória resistente"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      data={[result.verification.diagram.demandPoint]}
                      dataKey="y"
                      name="Ponto solicitante"
                      stroke="none"
                      dot={{ r: 6, fill: '#16a34a' }}
                      isAnimationActive={false}
                      legendType="none"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                {result.verification.demandStressDiagram && (
                  <>
                    <h3>Diagrama de tensões da seção</h3>
                    <p>
                      A seção é exibida rotacionada para que a linha neutra fique horizontal e o lado comprimido para
                      cima, com o diagrama parábola-retângulo de tensões desenhado a partir do canto mais comprimido.
                    </p>
                    <ChartLegend>
                      <span>
                        <span className="swatch" style={{ background: '#1d4ed8' }} /> Barra comprimida
                      </span>
                      <span>
                        <span className="swatch" style={{ background: '#f97316' }} /> Barra tracionada
                      </span>
                      <span>
                        <span className="swatch line" style={{ background: '#374151' }} /> Linha neutra
                      </span>
                      <span>
                        <span className="swatch" style={{ background: '#93c5fd' }} /> Diagrama de tensões
                        (parábola-retângulo)
                      </span>
                    </ChartLegend>
                    <ImageDiv>
                      <StressDiagram stressDiagram={result.verification.demandStressDiagram} />
                      <CopyImageButton
                        type="button"
                        title="Clique para copiar essa imagem"
                        onClick={() => handleCopySVG(stressDiagramRef)}
                      >
                        <FaRegCopy />
                        Copiar
                      </CopyImageButton>
                    </ImageDiv>
                    <p>{result.verification.demandStressDiagram.maxConcreteCompressionStress.label}</p>
                    <h3>Diagrama de deformações da seção</h3>
                    <ChartLegend>
                      <span>
                        <span className="swatch" style={{ background: '#6366f1' }} /> Deformação de compressão
                      </span>
                      <span>
                        <span className="swatch" style={{ background: '#f97316' }} /> Deformação de tração
                      </span>
                      <span>
                        <span className="swatch line" style={{ background: '#374151' }} /> Linha neutra
                      </span>
                    </ChartLegend>
                    <ImageDiv>
                      <StrainDiagram stressDiagram={result.verification.demandStressDiagram} />
                    </ImageDiv>
                    <h3>Tensões e deformações nas barras</h3>
                    <TableContainer>
                      <Table>
                        <thead>
                          <tr>
                            <Th>x (cm)</Th>
                            <Th>y (cm)</Th>
                            <Th>As (cm²)</Th>
                            <Th>Deformação (‰)</Th>
                            <Th>Tensão (MPa)</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.verification.demandStressDiagram.bars.map((bar, index) => (
                            <Tr key={index}>
                              <Td>{bar.x}</Td>
                              <Td>{bar.y}</Td>
                              <Td>{bar.As}</Td>
                              <Td>{(bar.strain * 1000).toFixed(2)}</Td>
                              <Td>{bar.stress}</Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Result>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  )
}
